import {
    type FC,
    Fragment,
    useContext,
    useState,
    useEffect,
    useTransition,
    useCallback,
    type ReactNode,
} from 'react';
import { Route } from 'react-router-dom';
import { graphql, useRefetchableFragment, useRelayEnvironment } from 'react-relay';
import serverVars from 'server-vars';
import { Toast } from 'dibs-toaster/exports/Toast.ts';
import { TabPanels } from 'dibs-elements/exports/TabPanels.tsx';
import { TabProvider } from 'dibs-elements/exports/TabProvider.ts';
import { BasicSelect } from 'dibs-elements/exports/BasicSelect.tsx';
import { useRefetchWithoutSuspense } from 'dibs-react-hooks/exports/useRefetchWithoutSuspense.ts';
import { NftItemTileContextProvider } from 'dibs-nft-item-tile/exports/NftItemTileContext.ts';
import { ServerVarsContext } from '../../../utils/ServerVarsContext.tsx';

import { NftProfileCollectionNav } from './NftProfileCollectionNav/NftProfileCollectionNav.tsx';
import { NftProfileCollectionNavImportCta } from './NftProfileCollectionNav/NftProfileCollectionNavImportCta.tsx';
import { NftProfileEmptyCollection } from './NftProfileEmptyCollection/NftProfileEmptyCollection.tsx';
import { NftProfileImportCollectionModalLazy } from './NftProfileSections/importCollection/NftProfileImportCollectionModalLazy.tsx';
import { useNftCollectorTabConfig } from './useNftCollectorTabConfig.tsx';
import { useProfileCollectionToast } from './useProfileCollectionToast.ts';
import { type TabRoute } from './useNftCollectorTabConfig.tsx';
import { refetchProfileCollectionTabQuery } from './refetchProfileCollectionTabQuery.ts';
import { refetchProfileDraftsTabQuery } from './refetchProfileDraftsTabQuery.ts';
import { useNftProfileOwner } from '../../helpers/useNftProfileOwner.tsx';

import { type NftProfileCollection_viewer$key } from './__generated__/NftProfileCollection_viewer.graphql.ts';
import { type NftProfileCollectionQuery } from './__generated__/NftProfileCollectionQuery.graphql.ts';

import styles from './NftProfileCollection.module.css';

const NftCollectorMobileSection: FC<{ sections: TabRoute['sections'] }> = ({ sections }) => {
    const visibleSections = sections.filter(({ isVisible }) => isVisible);

    const [activeTab, setActiveTab] = useState<string | undefined>(visibleSections[0]?.name);

    if (!activeTab) {
        return null;
    }

    return (
        <TabProvider activeTab={activeTab} setActiveTab={setActiveTab}>
            {visibleSections.length > 1 && (
                <div className={styles.selectContainer}>
                    <BasicSelect
                        value={activeTab}
                        dataTn="nft-collector-mobile-tab-select"
                        onChange={e => {
                            setActiveTab(e.target.value);
                        }}
                        options={visibleSections.map(({ name, title }) => ({
                            value: name,
                            label: title ?? '',
                        }))}
                    />
                </div>
            )}

            <TabPanels renderOnInactive>
                {visibleSections.map(({ name, node }): [string, ReactNode] => {
                    return [name, node];
                })}
            </TabPanels>
        </TabProvider>
    );
};

const NftCollectorSectionRoute: FC<{ tabConfig: TabRoute; isMobile: boolean }> = ({
    tabConfig,
    isMobile,
}) => {
    const { sections, path, name } = tabConfig;

    let sectionNode: ReactNode;
    if (isMobile && sections.length > 1) {
        sectionNode = <NftCollectorMobileSection sections={sections} />;
    } else {
        sectionNode = sections
            .filter(section => section.isVisible)
            .map(section => <Fragment key={section.name}>{section.node}</Fragment>);
    }

    return (
        <Route
            key={name}
            path={path}
            exact
            children={({ match }) => (
                <div style={{ display: match ? 'unset' : 'none' }}>{sectionNode}</div>
            )}
        />
    );
};

type Props = {
    viewer: NftProfileCollection_viewer$key;
    userIdentifiyingText: string;
    userHasMinterRole: boolean | null;
    profileUserId?: string | null;
    profileWalletAddress?: string | null;
};

export const NftProfileCollection: FC<Props> = ({
    viewer: viewerRef,
    userIdentifiyingText,
    userHasMinterRole,
    profileUserId,
    profileWalletAddress,
}) => {
    const environment = useRelayEnvironment();
    const [isCollectionPending, startTransition] = useTransition();
    const { isMobile } = useContext(ServerVarsContext);
    const { isProfileOwner, canImport } = useNftProfileOwner();
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);
    const [viewer, refetch] = useRefetchableFragment(
        graphql`
            fragment NftProfileCollection_viewer on Viewer
            @refetchable(queryName: "NftProfileCollectionQuery") {
                nftBenignNeglect: featureFlag(feature: "nftBenignNeglect")
                ...collectorTabConfig_viewer @relay(mask: false)
                ...NftItemTile_viewer
                ...NftProfileSectionCollection_viewer
                ...NftProfileSectionAuctions_viewer
                ...NftProfileSectionCreations_viewer
                ...NftProfileSectionLiveBids_viewer
                ...NftProfileSectionLostBids_viewer
                ...NftProfileSectionDrafts_viewer @include(if: $fetchDrafts)
                ...NftProfileCollectionNav_viewer
                ...NftProfileImportCollectionList_viewer
                ...NftProfileImportCollectionView_viewer
                ...NftProfileImportCollectionModal_viewer
            }
        `,
        viewerRef
    );

    const refetchProfileDraftsTab = useRefetchWithoutSuspense<NftProfileCollectionQuery>(
        refetchProfileDraftsTabQuery
    );
    const refetchProfileCollectionTab = useRefetchWithoutSuspense<NftProfileCollectionQuery>(
        refetchProfileCollectionTabQuery
    );

    useEffect(() => {
        if (isProfileOwner && userHasMinterRole) {
            const { username, walletAddress, tokenContractId, fetchUser, userId } =
                serverVars.get('relay.variables') ?? {};
            refetchProfileDraftsTab(
                {
                    username,
                    walletAddress,
                    fetchDrafts: true,
                    tokenContractId,
                    hasTokenContractId: !!tokenContractId,
                    fetchUser,
                    userId,
                },
                refetch
            );
        }
    }, [isProfileOwner, userHasMinterRole, environment, refetch, refetchProfileDraftsTab]);

    const refetchCollection = useCallback(
        async (options?: { refetchDrafts?: boolean }): Promise<void> => {
            const { username, walletAddress, tokenContractId, userId, fetchUser } =
                serverVars.get('relay.variables') ?? {};

            const refetchDrafts = options?.refetchDrafts || false;

            return new Promise(resolve => {
                refetchProfileCollectionTab(
                    {
                        username,
                        walletAddress,
                        fetchDrafts: refetchDrafts,
                        tokenContractId,
                        hasTokenContractId: !!tokenContractId,
                        userId,
                        fetchUser,
                    },
                    refetch,
                    () => resolve()
                );
            });
        },
        [refetch, refetchProfileCollectionTab]
    );

    const secondaryMarketRefetch = useCallback(
        (options?: { refetchDrafts?: boolean }): void => {
            const refetchDrafts = options?.refetchDrafts || false;

            if (refetchDrafts) {
                startTransition(
                    () =>
                        void refetch(
                            { fetchDrafts: refetchDrafts },
                            { fetchPolicy: 'network-only' }
                        )
                );
            } else {
                refetchCollection({ refetchDrafts: true });
            }
        },
        [refetch, refetchCollection, startTransition]
    );

    const collectorTabs = useNftCollectorTabConfig({
        viewer,
        enableDrafts: userHasMinterRole,
        isCollectionPending,
    });

    const nonEmptyTabs = collectorTabs.filter(tab => tab.isVisible);
    const noHistory = nonEmptyTabs.length === 0;

    let mainContent = null;

    if (noHistory) {
        mainContent = (
            <NftProfileEmptyCollection
                userText={userIdentifiyingText}
                profileUserId={profileUserId}
            />
        );
    } else {
        mainContent = collectorTabs.map(tab => (
            <NftCollectorSectionRoute tabConfig={tab} isMobile={!!isMobile} key={tab.name} />
        ));
    }

    const toastProps = useProfileCollectionToast();
    return (
        <div className={styles.container}>
            <div className={styles.navContainer}>
                {!noHistory && <NftProfileCollectionNav tabConfig={nonEmptyTabs} viewer={viewer} />}
                {canImport && !viewer.nftBenignNeglect && (
                    <NftProfileCollectionNavImportCta onClick={() => setIsImportModalOpen(true)} />
                )}
            </div>

            <NftItemTileContextProvider
                secondaryMarketRefetch={secondaryMarketRefetch}
                displayToastNotification={toastProps.displayToastNotification}
                profileWalletAddress={profileWalletAddress}
            >
                {mainContent}
            </NftItemTileContextProvider>
            {canImport && (
                <NftProfileImportCollectionModalLazy
                    viewer={viewer}
                    setOpen={setIsImportModalOpen}
                    isOpen={isImportModalOpen}
                    onCollectionClick={id => {
                        startTransition(() => {
                            refetch({ tokenContractId: id, hasTokenContractId: !!id });
                        });
                    }}
                />
            )}
            <Toast {...toastProps}>{toastProps.content}</Toast>
        </div>
    );
};
